/**
 * <DocCompletenessBadge> — scores a clinical/service note in-flow against the
 * program-specific MCP audit rubric. Drop next to any note textarea.
 *
 * Backend: POST /api/billing-ai/doc-completeness/check (structural + optional
 * Claude refinement, RAG-augmented).
 *
 * Behavior:
 *  - Re-scores on note change with 800ms debounce
 *  - Verdict: pass (≥0.8) / warn (0.5–0.8) / block (<0.5)
 *  - Parent can read `onResultChange` to gate Submit when verdict === 'block'
 */

import { useEffect, useRef, useState } from 'react';
import { Loader2, CheckCircle, AlertTriangle, X, FileText, Sparkles } from 'lucide-react';
import { getWeaveinApiUrl } from '@/src/config';

interface CheckResult {
  score: number;
  verdict: 'pass' | 'warn' | 'block';
  structural_pass: boolean;
  missing_elements: string[];
  suggestions: string[];
  used_llm: boolean;
}

interface Props {
  note: string;
  programType: 'ECM' | 'CS' | 'CHW' | 'Doula' | string;
  activityCode?: string;
  encounterId?: number;
  /** Use Claude refinement on borderline structural scores */
  useLlm?: boolean;
  onResultChange?: (r: CheckResult | null) => void;
  /** Minimum chars before scoring kicks in — default 60 */
  minChars?: number;
}

function authHeaders() {
  const token = localStorage.getItem('auth_token');
  const cboId = localStorage.getItem('active_cbo_id');
  return {
    'Content-Type': 'application/json',
    ...(token && { Authorization: `Bearer ${token}` }),
    ...(cboId && { 'X-CBO-ID': cboId }),
  };
}

export default function DocCompletenessBadge({
  note,
  programType,
  activityCode,
  encounterId,
  useLlm = false,
  onResultChange,
  minChars = 60,
}: Props) {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<CheckResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (timer.current) clearTimeout(timer.current);
    if (!note || note.trim().length < minChars) {
      setResult(null);
      onResultChange?.(null);
      return;
    }
    timer.current = setTimeout(async () => {
      setBusy(true);
      setError(null);
      try {
        const r = await fetch(getWeaveinApiUrl('/api/billing-ai/doc-completeness/check'), {
          method: 'POST',
          credentials: 'include',
          headers: authHeaders(),
          body: JSON.stringify({
            note,
            program_type: programType,
            activity_code: activityCode || null,
            encounter_id: encounterId || null,
            use_llm: useLlm,
          }),
        });
        if (!r.ok) throw new Error(`${r.status} ${r.statusText}`);
        const data = (await r.json()) as CheckResult;
        setResult(data);
        onResultChange?.(data);
      } catch (e: any) {
        setError(e.message || String(e));
        onResultChange?.(null);
      } finally {
        setBusy(false);
      }
    }, 800);
    return () => {
      if (timer.current) clearTimeout(timer.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [note, programType, activityCode, useLlm]);

  if (busy && !result) {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-paper-2 px-2 py-0.5 text-[11px] text-ink-500">
        <Loader2 className="h-3 w-3 animate-spin" />
        Scoring note…
      </span>
    );
  }
  if (error) return null;
  if (!result) return null;

  const meta = {
    pass: { cls: 'bg-emerald-50 text-emerald-700 border-emerald-200', Icon: CheckCircle, label: 'Complete' },
    warn: { cls: 'bg-amber-50 text-amber-700 border-amber-200', Icon: AlertTriangle, label: 'Incomplete' },
    block: { cls: 'bg-red-50 text-red-700 border-red-200', Icon: X, label: 'Insufficient' },
  }[result.verdict];

  const { cls, Icon, label } = meta;

  return (
    <div className="inline-block">
      <button
        type="button"
        onClick={() => setExpanded((e) => !e)}
        className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-medium ${cls}`}
        title="Click for documentation guidance"
      >
        <FileText className="h-3 w-3" />
        Note {label} · {(result.score * 100).toFixed(0)}%
        {result.used_llm && <Sparkles className="h-3 w-3" />}
        <Icon className="h-3 w-3" />
      </button>

      {expanded && (result.missing_elements.length > 0 || result.suggestions.length > 0) && (
        <div className="mt-2 max-w-md rounded-md border border-v2border bg-paper-2 p-2 text-xs">
          {result.missing_elements.length > 0 && (
            <div className="mb-1">
              <span className="font-medium text-ink">Missing:</span>{' '}
              <span className="text-ink-500">
                {result.missing_elements.map((e) => e.replace(/_/g, ' ')).join(', ')}
              </span>
            </div>
          )}
          {result.suggestions.length > 0 && (
            <ul className="space-y-0.5">
              {result.suggestions.slice(0, 4).map((s, i) => (
                <li key={i} className="flex items-start gap-1.5 text-ink-500">
                  <span>•</span>
                  <span>{s}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
